import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { EventEmitter } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of, firstValueFrom, lastValueFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { FieldsService } from './fields.service';
import merge from 'lodash/merge';
import _map from 'lodash/map';
import { omit } from 'lodash';
import { XMLParser } from 'fast-xml-parser';
import dayjs from 'dayjs';
import { jsonToSafeString } from '../utils/string-utils';

export interface User {
	domain: string | null;
	id: string | null;
	email: string | null;
	firstName: string | null;
	lastName: string | null;
	loggedin: boolean;
	permissions: string[] | null;
	username: string | null;
	locale: string;
	configs: any | null;
	group: string | null;
	defaultLibrary: string | null;
	browseFields: any[] | null;
	searchInputs: any[] | null;
	properties: any | null;
	systemPreferences: any[] | null;
	autologin: boolean;
	phone: string | null;
	theme: string | null;
	collectionField?: string | null;
	gaTrackingID: string | undefined;
	passwordDisabled?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class UserService {

	private anonymousUser: User = {
		domain: null,
		id: null,
		email: null,
		firstName: null,
		lastName: null,
		loggedin: false,
		permissions: null,
		username: null,
		locale: 'en',
		configs: null,
		group: null,
		defaultLibrary: null,
		browseFields: null,
		searchInputs: null,
		properties: null,
		systemPreferences: null,
		autologin: false,
		phone: null,
		theme: null,
		gaTrackingID: undefined,
		passwordDisabled: true
	};

	public user = { ...this.anonymousUser }
	// use EventEmitter, alternative to use BehaviorSubject
	//private userSubject = new BehaviorSubject<any>({ ...this.anonymousUser });
	//public user$ = this.userSubject.asObservable();
	//LOCALIZATION: BehaviorSubject<any> = new BehaviorSubject({});

	public LOCALIZATION: any = {}
	defaultLocale: string = "en";
	private csrfToken: string | null = null;
	broadcastEvent: EventEmitter<string> = new EventEmitter();
	broadcastParamsEvent: EventEmitter<{ type: string; data: any; options?: any; facetsField?: string }> = new EventEmitter();
	private defaultAccess = {
		level: 0,
		type: "PROHIBITED"
	};
	private prefsUpdateBroken: boolean = false;

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private fieldsService: FieldsService,
		private router: Router,
		private location: Location
	) {
		// TODO
		// legacy
		// Update the user's config-based fields based on the current domain config
		// $rootScope.$on("loggedin", function () {});
		// new
		// this.broadcastEvent.emit('loggedin');
	}

	/** Attempt public user login */
	attemptPublicUserLogin(): Promise<string> {
		return new Promise((resolve, reject) => {
			const publicConfig = this.configService.getConfig().auth.public;
			if (!publicConfig.autoEnabled) {
				resolve('no public');
				return;
			}
			this.http.post(`/aw-server/rest/register/user/publiclogin?user=${publicConfig.username}`, {}).subscribe(
				(userDrtailsResponse: any) => {
					this.user.loggedin = false;
					let response = this.decodeDetails(userDrtailsResponse);
					if (
						response.permissions.includes('ROLE_DATA_MANAGEMENT') ||
						response.permissions.includes('ROLE_PRODUCT_ADD')
					) {
						this.http.get(this.configService.getConfig().document.listConfigurationURL).subscribe(
							(configs: any) => {
								localStorage.setItem('configs', JSON.stringify(configs));
								response.configs = configs;
								this.processUserDetails(response);
								this.loadLocalization();
								this.anonymousUser = this.user; // Bind to public user
								resolve(this.user.username ?? '');
								this.broadcastEvent.emit('loggedin'); // Replaces $rootScope.$broadcast('loggedin')
							},
							(error) => {
								this.processUserDetails(response);
								this.loadLocalization();
								resolve(this.user.username ?? '');
								this.broadcastEvent.emit('loggedin');
								this.anonymousUser = this.user; // Bind to public user
							}
						);
					} else {
						this.processUserDetails(response);
						this.loadLocalization();
						resolve(this.user.username ?? '');
						this.broadcastEvent.emit('loggedin');
						this.anonymousUser = this.user; // Bind to public user
					}
				},
				() => {
					reject('Login failed');
					/*this.addServiceAlertEvent.emit({
					  alert: {
						title: 'System Error',
						message: 'Could not log in as public user.',
						delay: 5000,
					  },
					  type: 'error',
					});*/
				}
			);
		});
	}

	/** Load default configurations for the user */
	loadDefaults(): Promise<any> {
		if (
			this.user.permissions && (
				this.user.permissions.includes('ROLE_DATA_MANAGEMENT') ||
				this.user.permissions.includes('ROLE_PRODUCT_ADD'))
		) {
			return firstValueFrom(this.http.get(this.configService.getConfig().document.listConfigurationURL))
				.then((configs: any) => {
					localStorage.setItem('configs', JSON.stringify(configs));
					return configs;
				})
				.catch((error) => {
					console.error('Error loading configs:', error);
					return {};
				});
		} else {
			return Promise.resolve({});
		}
	}

	/** Process user details and update state */
	async processUserDetails(details: any): Promise<void> {
		let oldConfigs = this.user.configs ? this.user.configs : undefined;
		merge(this.user, details);
		this.user.permissions = details.permissions;
		this.user.passwordDisabled = !!details.passwordDisabled;
		this.user.group = details.group;
		this.user.configs = details.configs ? details.configs : oldConfigs
		this.user.defaultLibrary = details.properties.awlibraryObjectList[0]; //fall back to first available;
		var preferences = {
			result: {},
			search: {},
			metadata: {}
		};

		var listObjects: Record<string, any> = {};
		var fieldObjectList = details.properties.awfieldCollectionObjectList;
		for (var i = 0; i < fieldObjectList.length; i++) {
			var currField = fieldObjectList[i];
			listObjects[currField.field] = currField;
		}

		var subTypes = [];
		var fieldObjectList = details.properties.awfieldObjectList;
		var allObjects: Record<string, any> = {};
		for (var i = 0; i < fieldObjectList.length; i++) {
			var fieldObject = fieldObjectList[i];
			allObjects[fieldObject.name] = fieldObject;
			fieldObject.dependents = {};
		}

		for (var i = 0; i < fieldObjectList.length; i++) {
			var currField = fieldObjectList[i];
			if (listObjects[currField.name]) {
				currField.AWListObject = listObjects[currField.name];
			}
		}

		for (var fieldName in allObjects) {
			var currField = allObjects[fieldName];

			if (currField.AWListObject && currField.AWListObject.parent && currField.AWListObject.parent != "") {
				var parent = allObjects[currField.AWListObject.parent];
				if (parent)
					parent.dependents[currField.name] = currField;
				currField.parent = parent;
			}
			if (currField.AWListObject && currField.AWListObject.element) {
				for (var x = 0; x < currField.AWListObject.element.length; x++) {
					var option = currField.AWListObject.element[x];
					if (option.childs && option.childs.length > 0) {
						for (var c = 0; c < option.childs.length; c++) {
							var child = option.childs[c];
							currField.dependents[child.name] = allObjects[child.name];
						}
					}
				}
			}
		}

		for (var fieldName in allObjects) {
			var currField = allObjects[fieldName];
			if (currField.dependents) {
				for (var d in currField.dependents) {
					if (currField.dependents[d])
						currField.dependents[d].parent = currField;
				}
			}
		}

		for (var fieldName in allObjects) {
			var currField = allObjects[fieldName];
			var system = currField.AWListObject;

			currField.searchField = {
				type: system ? (system.type) : currField.type,
				title: currField.displayName ? currField.displayName : currField.AWListObject.displayName,
				options: currField.AWListObject ? currField.AWListObject.element : null,
				nullMessage: " ",
				level: this.getLevel(currField),
				name: currField.name,
				dependents: currField.dependents,
				parent: currField.parent,
				subType: currField.subType,
				list: currField.type == "STRING" ? [{}] : null,
				dcName: currField.dcname
			};
		}

		this.user.systemPreferences = fieldObjectList;
		var searchValues: any[] = fieldObjectList.filter(function(x: any) { return true; }).map(
			function(x: any) {
				if (x.searchField) {
					x.searchField.hidden = !(x.isAvailable && x.isSearch);
					return x.searchField;
				}
			});
		this.user.searchInputs = searchValues;

		this.user.browseFields = [];
		for (var i = 0; i < details.properties.awlibraryObjectList.length; i++) {
			var libobj = details.properties.awlibraryObjectList[i];
			for (var j = 0; j < libobj.awhierarchyObject.length; j++) {
				var browse = libobj.awhierarchyObject[j];
				if (this.user.browseFields.indexOf(browse.hierarchyName) == -1) {
					this.user.browseFields.push(browse.hierarchyName);
				}
			}
		}

		for (var i = 0; i < details.properties.awpropertyObjectList.length; i++) {
			var propobj = details.properties.awpropertyObjectList[i];
			if ("USER_INTERFACE_LANGUAGE" == propobj.name) {
				if (propobj.value != this.user.locale) {
					this.user.locale = propobj.value;
					this.loadLocalization();
				}
				break;
			}
		}

		await this.configService.loadConfiguration();

		if (this.configService.getConfig().search.sortFields) {
			searchValues = this.user.searchInputs;
			this.user.searchInputs = searchValues.sort(
				function(a: any, b: any): number {
					if (a.level < b.level) {
						return -1;
					} else if (a.level > b.level) {
						return 1;
					} else if (a.level == b.level) {
						if (a.title > b.title) {
							return 1;
						} else if (a.title < b.title) {
							return -1;
						} else {
							return 0;
						}
					} else {
						return 0;
					}
				});
		}

		// Adds additional properties and functions to the searchInput fieldset used throughout the app
		this.fieldsService.decorateUserSearchInputs(this.user.searchInputs);

		//this.user = ({ ...this.user, loggedin: true });
		this.broadcastEvent.emit('userPrefsUpdate');
	}

	private getLevel(currField: any): number {
		if (!currField || !currField.parent && Object.keys(currField.dependents).length == 0) {
			return 0;
		}
		return 1 + this.getLevel(currField.parent);
	}


	/** Load localization settings */
	loadLocalization(): void {
		type LocalizationResult = { [key: string]: string };
		if (this.user && this.user.locale !== this.defaultLocale) {
			this.loadLocalizationFor(this.defaultLocale).pipe(
				switchMap((defaultSettings) => {
					return this.loadLocalizationFor(this.user.locale).pipe(
						catchError(() => {
							console.error(`LOCALIZATION (${this.user.locale}) retrieval failed!`);
							this.LOCALIZATION = { ...defaultSettings };
							this.broadcastEvent.emit('localizationLoaded');
							return of(defaultSettings);
						})
					);
				})
			).subscribe(
				(result: LocalizationResult) => {
					this.LOCALIZATION = { ...result };
					this.broadcastEvent.emit('localizationLoaded');
					console.log(`LOCALIZATION (${this.user.locale}) settings loaded.`);
				},
				(err) => {
					console.error(`LOCALIZATION (${this.defaultLocale}) retrieval failed!`);
				}
			);
		} else {
			this.loadLocalizationFor(this.user.locale).subscribe(
				(result: LocalizationResult) => {
					this.LOCALIZATION = { ...result };
					this.broadcastEvent.emit('localizationLoaded');
					console.log(`LOCALIZATION (${this.user.locale}) settings loaded.`);
				},
				(err) => {
					console.error(`LOCALIZATION (${this.user.locale}) retrieval failed!`);
				}
			);
		}
	}

	loadLocalizationFor(locale: string): Observable<any> {
		return this.http.get(`~/../app/load/localization_${locale}.xml`, { responseType: 'text' }).pipe(
			switchMap((response) => {
				const parser = new XMLParser();
				const obj = parser.parse(response);
				//const obj = JSON.parse(xml2json(response,{ compact: true, spaces: 4 }));
				if (obj && obj.settings) {
					return of(obj.settings);
				} else {
					console.error(`LOCALIZATION (${locale}) settings missing!`);
					return of({});
				}
			}),
			catchError(() => {
				console.error(`LOCALIZATION (${locale}) retrieval failed!`);
				return of({});
			})
		);
	}

	/** Fetch user data */
	async whenGetUserdata(): Promise<any> {
		try {
			const userDetailsResponse = await firstValueFrom(this.http.post<any>(this.configService.getConfig().auth.userDetailsURL, {}));
			const publicConfig = this.configService.getConfig().auth.public;
			let response = this.decodeDetails(userDetailsResponse);
			if (publicConfig.autoEnabled && response?.username === publicConfig.username) {
				this.user.loggedin = false;
			} else if (response?.username) {
				this.user.username = response.username;
				this.user.loggedin = true;
			} else {
				this.user.username = "";
				this.user.loggedin = false;
			}
			
			if (response.isAutoLoggedIn) {
				this.user.autologin = true;
			}			

			// Load extra config if the user has permissions
			if (response.permissions.includes('ROLE_DATA_MANAGEMENT') || response.permissions.includes('ROLE_PRODUCT_ADD')) {
				try {
					const configData = await this.loadUserConfigs();
					response.configs = configData;

					// Save to localStorage
					localStorage.setItem('configs', JSON.stringify(configData));
				} catch (error) {
					console.error('Failed to load user configs:', error);
				}
			}

			return response;
		} catch (error) {
			console.error('User data retrieval failed:', error);
			this.user.username = "";
			this.user.loggedin = false;
			throw error;
		}
	}

	private loadUserConfigs(): Promise<any> {
		const configUrl = this.configService.getConfig().document.listConfigurationURL;
		return firstValueFrom(this.http.get<any>(configUrl));
	}

	processToken(): Promise<string> {
		localStorage.removeItem("csrf-token");

		// Make the HTTP request and convert it to a Promise using lastValueFrom
		return lastValueFrom(
			this.http.get<string>('/aw-server/rest/user/csrf-token', { responseType: 'text' as 'json' })  // Get CSRF token as string
		)
			.then((token: string) => {  // The response is now the token as string
				localStorage.setItem('csrf-token', token);
				return 'Login Successful';  // Return success message
			})
			.catch((error: HttpErrorResponse) => {  // Handle HTTP error
				if (error.status === 200) {
					localStorage.setItem('csrf-token', error.error);
					return 'Token received';
				} else {
					console.error("Error processing token:", error);
					return 'Could not read csrf-token, for whatever reason';
				}
			});
	}

	logout(isAutoLogin: boolean = false, isGoHome: boolean = true): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.http.post('/aw-server/logout', {}).subscribe(
				() => {
					if (!!this.user.username) {
						localStorage.removeItem(this.user.username);
					}
					localStorage.removeItem("csrf-token");
					this.resetUserToAnonymous(); // Reset user to anonymous
					this.broadcastEvent.emit('loggedout'); // Notify listeners
					if(isGoHome){
						this.goHome(isAutoLogin);
					}
					resolve(true);
				},
				(err: any) => {
					if (!!this.user.username) {
						localStorage.removeItem(this.user.username);
					}
					localStorage.removeItem("csrf-token");
					this.resetUserToAnonymous(); // Reset user to anonymous
					this.broadcastEvent.emit('loggedout'); // Notify listeners
					//TODO navigate to the login
					//this.router.navigate(['home']); // Redirect to home
					//$state.go("home");
					if(isGoHome){
						this.goHome(isAutoLogin);
					}
					resolve(false);
				}
			);
		});
	}
	
	resetUserToAnonymous(): void {
		this.user = { ...this.anonymousUser };
	}
    isAnonymusUser(){
		return this.user && this.user.id && this.user.id === this.anonymousUser.id;
	}
    
	goHome(sAutoLogin: boolean = false) {
		if (this.user?.loggedin || this.configService.getConfig().auth?.public?.autoEnabled) {
			this.router.navigate(['']);
		} else if (sAutoLogin) {
			this.router.navigate(['/login'], { queryParams: { "hide": "y" } });
		} else {
			this.router.navigate(['/login']);
		}
	}


	async login(username: string, password: string, isPublic: boolean, isAutoLogin: boolean = false): Promise<string> {
		const loginUrl = '/aw-server/rest/register/user/details?passwordResetURL=' + encodeURIComponent(location.pathname + this.router.createUrlTree(['reset']).toString());
		let headers = new HttpHeaders().set('Authorization', 'Basic ' + btoa(username + ':' + password));
		if (isAutoLogin) {
			headers = headers.set('X-AutoLogin', 'true');
		}

		return firstValueFrom(this.http.post<any>(loginUrl, null, { headers })).then(
			async (userDetailsResponse) => {
				const isDisclaimerAccepted = localStorage.getItem('kv_disclaimer') === 'accepted';
				const cookiesAllowed = sessionStorage.getItem('cookiesAllowed') === 'true';
				const response = this.decodeDetails(userDetailsResponse);
				if (isDisclaimerAccepted) {
					localStorage.setItem('kv_disclaimer', 'accepted');
				}
				if (cookiesAllowed) {
					sessionStorage.setItem('cookiesAllowed', 'true');
				}

				return this.processToken().then(async () => {
					if (isPublic) {
						this.user.loggedin = false;
					} else {
						this.user.loggedin = true;
					}
					
					if (response.isAutoLoggedIn) {
						this.user.autologin = true;
					}
					
					if (response.permissions.includes('ROLE_DATA_MANAGEMENT') || response.permissions.includes('ROLE_PRODUCT_ADD')) {
						return firstValueFrom(this.http.get<any>(this.configService.getConfig().document.listConfigurationURL))
							.then(async (configs) => {
								localStorage.setItem('configs', JSON.stringify(configs));
								response.configs = configs;
								await this.processUserDetails(response);
								this.loadLocalization();
								this.broadcastEvent.emit('loggedin');
								return Promise.resolve(this.user.username || "");  // Correctly return a Promise<string>
							})
							.catch(async () => {
								await this.processUserDetails(response);
								this.loadLocalization();
								this.broadcastEvent.emit('loggedin');
								return Promise.resolve(this.user.username || "");  // Correctly return a Promise<string>
							});
					} else {
						await this.processUserDetails(response);
						this.loadLocalization();
						this.broadcastEvent.emit('loggedin');
						return Promise.resolve(this.user.username || "");  // Correctly return a Promise<string>
					}
				});
			})
			.catch(response => {
				if (response.headers && response.headers.has('REDIRECT')) {
					window.location.href = response.headers.get('REDIRECT');
					return Promise.reject("");  // Ensure rejected promise returns a string
				} else {
					return this.processToken().then(() => {
						this.resetUserToAnonymous(); // Reset user
						this.loadLocalization();
						return Promise.reject(response);  // Return a rejected Promise with error data
					});
				}
			})
	}

	autologin(token: string, criteria: any): Promise<void> {
		return firstValueFrom(this.http.get<any>(`/aw-server/rest/register/user/autologin/token/${encodeURIComponent(token)}`))
			.then(response => {
				return this.logout(true).then(() => {
					return this.login(response.username, atob(response.password), false, true)
						.then(() => {
							this.user.autologin = true;
							this.broadcastParamsEvent.emit({ type: 'autologin', data: { 'data': response, criteria } });
						})
						.catch(() => {
							this.goHome();
							// TODO do we need to show alert?
							// legacy
							/*var alert1 = {
								title: "Invalid token",
								message: "Failed to process request based on token.",
								delay: 5000
							};*/
							// new
							/*alertsService.error({
								message: $scope.LOCALIZATION.invalidToken;
							});*/
						});
				});
			})
			.catch(() => {
				this.loadLocalization();
				this.broadcastEvent.emit('login');
			});
	}

	// library access level for this user;
	getAccessLevel(libraryName: string): number {
		let acl = 0;

		if (this.user?.properties?.awlibraryObjectList) {
			const lib = this.user.properties.awlibraryObjectList.find((lib: any) => lib.name === libraryName);
			if (lib) {
				acl = lib.accessLevel;
			}
		}
		return acl;
	}

	getProductAccessLevel(recordMetadata: any): number {
		return recordMetadata.access_level ? Number(recordMetadata.access_level) : this.defaultAccess.level;
	}
	// use searchService function. This is temporary function
	validateSearchStateParams(params: any | null): any {
		params = (params || {});
		// Always include a view property
		if (!params.hasOwnProperty('v') || params.v === undefined) {
			// console.log('svc.resultView = %s, $stateParams.v = %s', svc.resultView, $stateParams.v);
			let v = 'G';
			/*if ($stateParams.v) {
				v = $stateParams.v;
			} else if (svc.resultView) {
				v = svc.resultView;
			}*/
			if (this.user?.properties) {
				for (var prop in this.user.properties.awpropertyObjectList) {
					var property = this.user.properties.awpropertyObjectList[prop];
					if (property.name == 'RESULT_LIST_DISPLAY_TYPE') {
						switch (property.value) {
							case '1':
								v = "G";
								break;
							case '2':
								v = "L";
								break;
							case '3':
								v = "T";
								break;
							case '4':
								v = (this.configService.getConfig()?.esri?.enabled) ? "M" : "G";
								break;
							default:
								v = "G";
						}
					}
				}
			}
			params.v = v;
		}
		return params;
	};

	getUserExtendedPrefs(): Promise<object> {
		return new Promise<any>((resolve, reject) => {
			// Check if user is blocked from non-public options or if preferences update is broken
			if (this.user.permissions && this.user.permissions.includes("BLOCK_NON_PUBLIC_USER_OPTIONS") || this.prefsUpdateBroken) {
				const storedPrefs = sessionStorage.getItem(`extendedPrefs_${this.user?.username}`);
				const userSessionData = storedPrefs ? JSON.parse(storedPrefs) : [];
				resolve(userSessionData);
			} else {
				// Proceed with HTTP request to fetch preferences
				this.http.get('/aw-server/rest/register/user/extended/prefs/user').pipe(
					catchError(err => {
						console.error(err);  // Log the error
						this.prefsUpdateBroken = true;
						const errorMessage = err?.message || 'Failed to get user preferences';
						reject(errorMessage);  // Reject with error message
						return of(null);  // Return a fallback observable to satisfy the stream
					})
				).subscribe(response => {
					if (response) {
						resolve(response);
					}
				});
			}
		});
	}

	updateUserExtendedPrefs(payload: any): Promise<boolean> {
		return new Promise<any>((resolve, reject) => {
			if ((this.user?.permissions?.includes("BLOCK_NON_PUBLIC_USER_OPTIONS")) || this.prefsUpdateBroken) {
				const userprefs = { prefs: payload };
				const storedPrefs = JSON.stringify(userprefs);
				sessionStorage.setItem(`extendedPrefs_${this.user?.username}`, storedPrefs);
				resolve(true);
			} else {
				const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
				this.http.put('/aw-server/rest/register/user/extended/prefs/update',
					JSON.stringify(payload), { headers })
					.pipe(
						catchError(err => {
							if (this.user?.username) {
								const userprefs = { prefs: payload };
								const storedPrefs = JSON.stringify(userprefs);
								sessionStorage.setItem(`extendedPrefs_${this.user.username}`, storedPrefs);
							}
							this.prefsUpdateBroken = true;
							console.error(err)
							reject("Failed to update user preferences via endpoint...");
							return of(null);
						})
					).subscribe(() => {
						resolve(true);
					});
			}
		});
	}

	updatePartialUser(payload: any): Promise<boolean> {
		return new Promise<any>((resolve, reject) => {
			if (this.user?.permissions?.includes("BLOCK_NON_PUBLIC_USER_OPTIONS")) {
				// Do nothing            
				resolve(true);
			} else {
				const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
				this.http.put('/aw-server/rest/register/user/update/partial',
					JSON.stringify(payload), { headers })
					.pipe(
						catchError(err => {
							reject("Failed to update user profile.");
							return of(null);
						})
					).subscribe(response => {
						let responseUser = response ? ((typeof response === 'object') ? response : JSON.parse(response as string)) : null;
						if (responseUser) {
							this.user.firstName = responseUser.firstName;
							this.user.lastName = responseUser.lastName;
							this.user.email = responseUser.email;
							this.user.phone = responseUser.phone;
							this.user.theme = responseUser.theme;
						} else {
							console.error("Failed to parse user response");
						}
						resolve(true);
					});
			}
		});
	}

	registerUser(payload: any, answer: string): Promise<boolean> {
		let publicConfig = this.configService.getConfig().auth.public;
		return new Promise<boolean>((resolve, reject) => {
			this.http.get<boolean>(`/aw-server/rest/register/user/captcha/status?answer=${encodeURIComponent(answer)}`)
				.pipe(
					catchError(err => {
						console.error(err);  // Log the error
						reject("Form submission failed");
						return of(null);  // Return a fallback observable to satisfy the stream
					})
				).subscribe(response => {
					if (response) {
						const headers = new HttpHeaders({
							"Content-Type": "application/json",
						});

						if (publicConfig.autoEnabled) {
							headers.set("Authorization", "Basic " + btoa(publicConfig.username + ':' + publicConfig.password));
						}
						this.http.put('/aw-server/rest/register/user/add',
							JSON.stringify(payload), { headers })
							.pipe(
								catchError(err => {
									if (err && err.fieldErrors?.length > 0) {
										reject(err.fieldErrors[0].message);
									} else if (err && err.message) {
										reject(err.message);
									} else if (err) {
										reject(err);
									} else {
										reject("Failed to add user.");
									}
									return of(null);
								})
							).subscribe(response => {
								resolve(true);
							});

					} else {
						reject("Failed to get request status.");
					}
				});
		});
	}
	updatePassword(payload: any, oldpass: string, expired: boolean): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			const headers = new HttpHeaders({
				"Content-Type": "application/json",
			});
			this.http.put(`/aw-server/rest/register/user/update/ps?oldps=${encodeURIComponent(oldpass)}&expired=${expired}`,
				JSON.stringify(payload), { headers })
				.pipe(
					catchError(err => {
						console.error("Password update error:", err);
						if (err?.error?.fieldErrors?.length > 0) {
							reject(err.error.fieldErrors[0].message);
						} else if (err?.error?.message) {
							reject(err.error.message);
						} else {
							reject("Failed to update password.");
						}
						return of(null);
					})
				).subscribe({
					next: response => {
						// Attempt login after password update
						this.login(payload.username, payload.password, false)
							.then(() => resolve(true))
							.catch(() => reject("Failed to login."));
					},
					error: () => reject("Failed to update password.")
				});
		});
	}
	recoverUsername(email: string, firstname: string, lastname: string, answer: string): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			this.http.get<boolean>(`/aw-server/rest/register/user/captcha/status?answer=${encodeURIComponent(answer)}`)
				.pipe(
					catchError(err => {
						console.error(err);  // Log the error
						reject("Incorrect captcha answer.");
						return of(null);  // Return a fallback observable to satisfy the stream
					})
				).subscribe(response => {
					if (response) {
						this.http.get(`/aw-server/rest/register/user/username/request?email=${encodeURIComponent(email)}&firstName=${encodeURIComponent(firstname)}&lastName=${encodeURIComponent(lastname)}`)
							.pipe(
								catchError(err => {
									if (err && err.fieldErrors?.length > 0) {
										reject(err.fieldErrors[0].message);
									} else if (err && err.message) {
										reject(err.message);
									} else if (err) {
										reject(err);
									} else {
										reject("Could not send recovery email.");
									}
									return of(null);
								})
							).subscribe(response => {
								resolve(true);
							});
					} else {
						reject("Incorrect captcha answer.");
					}
				});
		});
	}
	recoverPassword(username: string, email: string, path: string, answer: string): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			this.http.get<boolean>(`/aw-server/rest/register/user/captcha/status?answer=${encodeURIComponent(answer)}`)
				.pipe(
					catchError(err => {
						console.error(err);  // Log the error
						reject("Incorrect captcha answer.");
						return of(null);  // Return a fallback observable to satisfy the stream
					})
				).subscribe(response => {
					if (response) {
						this.http.get(`/aw-server/rest/register/user/reset/request?username=${encodeURIComponent(username)}&email=${encodeURIComponent(email)}&path=${encodeURIComponent(path)}`)
							.pipe(
								catchError(err => {
									if (err && err.fieldErrors?.length > 0) {
										reject(err.fieldErrors[0].message);
									} else if (err && err.message) {
										reject(err.message);
									} else if (err) {
										reject(err);
									} else {
										reject("Could not send recovery link email.");
									}
									return of(null);
								})
							).subscribe(response => {
								resolve(true);
							});
					} else {
						reject("Incorrect captcha answer.");
					}
				});
		});
	}
	setNewPassword(username: string, password: string, sessionkey: string): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			const headers = new HttpHeaders({
				"Content-Type": "application/json",
			});
			this.http.post(`/aw-server/rest/register/user/reset?username=${encodeURIComponent(username)}&ps=${encodeURIComponent(password)}&key=${sessionkey}`, {})
				.pipe(
					catchError(err => {
						console.error("Password update error:", err);
						if (err?.error?.fieldErrors?.length > 0) {
							reject(err.error.fieldErrors[0].message);
						} else if (err?.error?.message) {
							reject(err.error.message);
						} else {
							reject("Failed to set new password.");
						}
						return of(null);
					})
				).subscribe(response => {
					resolve(true);
				});
		});
	}

	getRequestStatus(key: string): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.http.get<boolean>(`/aw-server/rest/register/user/reset/request/expired?key=${encodeURIComponent(key)}`)
				.pipe(
					catchError(err => {
						console.error(err);  // Log the error
						if (err.error?.fieldErrors?.length > 0) {
							reject(err.error.fieldErrors[0].message);
						} else if (err.error?.message) {
							reject(err.error.message);
						} else {
							reject("Failed to get request status.");
						}
						return of(null);  // Return a fallback observable to satisfy the stream
					})
				).subscribe(response => {
					if (response !== null && response !== undefined) {
						resolve(response);
					} else {
						reject("Failed to get request status.");
					}
				});
		});
	}

	async updateUser(payload: any): Promise<boolean> {
		let libDisplayNameEmpty = false;
		let jsonPayload = payload.prefs ? JSON.parse(payload.prefs) : {};

		if (jsonPayload.awlibraryObjectList) {
			jsonPayload.awlibraryObjectList.forEach((lib: any) => {
				if (!lib.displayName || lib.displayName.length === 0) {
					libDisplayNameEmpty = true;
				}
			});
		}

		if (libDisplayNameEmpty) {
			return Promise.reject('One of the library display names is not filled.');
		}

		try {
			const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

			// Send the PUT request
			payload.prefs = jsonToSafeString(payload.prefs);
			const response: any = await firstValueFrom(
				this.http.put('/aw-server/rest/register/user/update', payload, { headers, responseType: 'text' })
			);

			const user = JSON.parse(response);
			const details: any = {
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
				phone: user.phone,
				properties: JSON.parse(user.prefs),
				permissions: this.user.permissions
			};

			if (user.userGroup) {
				details.group = user.userGroup.name;
			}

			// Check user permissions and load configuration
			if (details.permissions.includes('ROLE_DATA_MANAGEMENT') || details.permissions.includes('ROLE_PRODUCT_ADD')) {
				try {
					const configs: any = await firstValueFrom(this.http.get(this.configService.getConfig().document.listConfigurationURL));
					localStorage.setItem('configs', JSON.stringify(configs));
					details.configs = configs;
				} catch (error) {
					console.warn('Failed to load configurations, proceeding without them.');
				}
			}

			await this.processUserDetails(details);
			this.loadLocalization();

			return true;
		} catch (error: any) {
			if (error?.error?.fieldErrors?.length > 0) {
				return Promise.reject(error.error.fieldErrors[0].message);
			} else if (error?.error?.message) {
				return Promise.reject(error.error.message);
			} else if (error?.error) {
				return Promise.reject(error.error);
			} else {
				return Promise.reject('Failed to update user.');
			}
		}
	}

	getPasswordPolicies(): Promise<any> {
		return firstValueFrom(this.http.get<any>(`/aw-server/rest/register/user/password/policies`));
	}

	getLocalStorageData(): any {
		var userDataCache = {
			visited: {},
			cols: {},
			theme: undefined,
			viewer: {}
		};
		if (this.user.username) {
			if (!localStorage[this.user.username]) {
				localStorage[this.user.username] = JSON.stringify({
					visited: {},
					cols: {},
					theme: undefined,
					viewer: {}
				});
			};
			userDataCache = JSON.parse(localStorage[this.user.username]);
			if (!userDataCache.viewer) userDataCache.viewer = {}
		}
		return userDataCache;
	};

	setLocalStorageData(json: any): void {
		if (this.isNotBlank(this.user.username)) {
			if (json && !(typeof json === 'string')) {
				json = JSON.stringify(json)
			};
			if (!!this.user.username) {
				localStorage[this.user.username] = json;
			}
		}
	};

	// TODO validate  
	isFacadeEnabled(toolName: string): Promise<boolean> {
		const url = `${this.configService.getConfig().auth.facadeEnabledURL}?name=${encodeURIComponent(toolName)}`;
		return firstValueFrom(this.http.get<boolean>(url)).then(response => {
			return response ?? false;
		})
			.catch(() => { return false; }); // If request fails, return false
	}

	getDateFormat(target?: string): string {
		var dateFormatDisplay = "MM/DD/YY";
		var property = this.user.properties ?
			this.user.properties.awpropertyObjectList.find(function(x: any) { return x.name == "USER_INTERFACE_DATE_TYPE"; }) : undefined;
		if (property && property.value) {
			dateFormatDisplay = property.value;
		}

		if ('datepicker' === target) {
			dateFormatDisplay = this.convertDayjsToFlatpickrFormat(dateFormatDisplay);
		}

		return dateFormatDisplay;
	}

	convertDayjsToFlatpickrFormat(dayjsFormat: string) {
		const tokenMap: { [key: string]: string } = {
			// Day.js to Flatpickr mapping
			'YYYY': 'Y',
			'YY': 'y',
			'MMMM': 'F',
			'MMM': 'M',
			'MM': 'm',
			'M': 'n',
			'DD': 'd',
			'D': 'j',
			'dddd': 'l',
			'ddd': 'D',
			'dd': 'D',
			'd': 'w',
			'HH': 'H',
			'H': 'G',
			'hh': 'h',
			'h': 'g',
			'mm': 'i',
			'm': 'i',
			'SSS': '', // Miliseconds are not supported
			'ZZ': '',  // Offset not supported
			'Z': '',   // Offset not supported
			'ss': 'S',
			's': 'S',
			'A': 'K',
			'a': 'K'
		};

		return dayjsFormat.replace(
			new RegExp(Object.keys(tokenMap).join('|'), 'g'),
			(match: string) => tokenMap[match as keyof typeof tokenMap] || match
		);
	}

	getDatePickerOptions(min?: string, max?: string, drops?: any, name?: string): any {
		// 'drops' is not supported. Natively handled by flatpickr
		// If you provide a string, it must match the date format string set in your locale setting.
		// Incoming min/max format is MM/DD/YYYY
		let dateFormat = "MM/DD/YYYY";
		let start = (min && min.toUpperCase() !== 'TODAY') ? dayjs(min, dateFormat) : dayjs();

		let startDateFields = this.configService.getConfig().search.date?.fieldsList ? this.configService.getConfig().search.date.fieldsList.split(",") : [];

		if (this.configService.getConfig().search.date?.startDate && startDateFields.includes(name)) {
			start = dayjs(this.configService.getConfig().search.date.startDate);
		}

		// Configure Flatpickr options
		const options = {
			dateFormat: this.getDateFormat('datepicker'),
			defaultDate: start.toDate(),
			minDate: min ? (min.toUpperCase() === 'TODAY' ? dayjs().toDate() : dayjs(min, dateFormat).toDate()) : null,
			maxDate: max ? (max.toUpperCase() === 'TODAY' ? dayjs().toDate() : dayjs(max, dateFormat).toDate()) : null,
			disableMobile: "true",
			allowInput: true,
			ranges: {}
		};

		// TODO: not natively supported by the flatpickr dialog
		if (this.configService.getConfig().search.date?.defaultDateRanges) {
			options.ranges = {
				Today: [dayjs().toDate(), dayjs().toDate()],
				Yesterday: [dayjs().subtract(1, 'day').toDate(), dayjs().subtract(1, 'day').toDate()],
				'Last Week': [dayjs().subtract(7, 'day').toDate(), dayjs().subtract(7, 'day').toDate()],
				'Last Month': [dayjs().subtract(1, 'month').toDate(), dayjs().subtract(1, 'month').toDate()]
			};
		}

		return options;
	}

	getDashboard(id: number): Promise<any> {
		return new Promise<any>((resolve, reject) => {
			if (!isFinite(id)) {
				reject(false);
				return;
			}

			firstValueFrom(this.http.get<any>(`/aw-server/rest/dashboard/${id}`))
				.then((response) => {
					try {
						if (response) {
							response.config = JSON.parse(response.config);
						}

						// Normalize fragment markup data
						if (response && !response.config || !response.config.ui_fragments) {
							response.config.ui_fragments = [];
						}
						if (response) {
							response.config.ui_fragments_decoded = {};
							for (const fragment of response.config.ui_fragments) {
								const fragmentId = fragment.id;
								// Decode from Base64
								const markup = atob(fragment.markup);
								response.config.ui_fragments_decoded[fragmentId] = markup;
							}
						}
					} catch (e) {
						if (response) {
							response.config = {};
						}
					}

					resolve(response);
				})
				.catch(() => reject({}));
		});
	}

	/** Convenience method to return a user-friendly user permissions object */
	getUserPermissionsMap(): Record<string, number> {
		if (this.user?.permissions) {
			return this.user.permissions.reduce((map: any, permission: string) => {
				map[permission] = 1;
				return map;
			}, {});
		}
		return {};
	}
	isNotBlank(str: string | null | undefined): boolean {
		return !!str && str.trim().length > 0;
	}
	triggerRegister(): void {
		this.broadcastEvent.emit('register');
	}
	runBroadcastEvent(eventId: string) {
		this.broadcastEvent.emit(eventId);
	}

	/**
	 *  Remove non-serializable properties, such as $$hashKey
	 */
	cleanData<T>(data: T): T {
		if (Array.isArray(data)) {
			return data.map(item => this.cleanData(item as any)) as T;
		} else if (typeof data === 'object' && data !== null) {
			const cleanedObj: any = omit(data, '$$hashKey');

			// Recursively clean nested objects and arrays
			Object.keys(cleanedObj).forEach(key => {
				cleanedObj[key] = this.cleanData(cleanedObj[key]);
			});

			return cleanedObj;
		}
		return data;
	}
	
decodeDetails(details: any): any {
	const decodedDetails: any = {};
	if (details?.username) {
		decodedDetails.username = atob(details.username);
	}
	if (details?.firstName) {
		decodedDetails.firstName = atob(details.firstName);
	}
	if (details?.lastName) {
		decodedDetails.lastName = atob(details.lastName);
	}
	if (details?.email) {
		decodedDetails.email = atob(details.email);
	}
	if (details?.phone) {
		decodedDetails.phone = atob(details.phone);
	}
	if (details?.domain) {
		decodedDetails.domain = atob(details.domain);
	}

	if (details?.permissions) {
		try {
			decodedDetails.permissions = JSON.parse(atob(details.permissions));
		} catch {
			console.warn('Invalid base64 JSON in permissions');
		}
	}

	if (details?.properties) {
		try {
			decodedDetails.properties = JSON.parse(atob(details.properties));
		} catch {
			console.warn('Invalid base64 JSON in properties');
		}
	}

	// Directly assign non-encoded fields
	decodedDetails.theme = details?.theme ?? null;
	decodedDetails.passwordDisabled = details?.passwordDisabled ?? false;
	decodedDetails.id = details?.id ?? null;
	decodedDetails.lastAccess = details?.lastAccess ?? null;
	decodedDetails.role = details?.role ?? null;
	decodedDetails.gaTrackingID = details?.gaTrackingID ?? null;
	decodedDetails.collectionField = details?.collectionField ?? null;
	decodedDetails.isAutoLoggedIn = details?.isAutoLoggedIn ?? false;

	return decodedDetails;
}

private isBase64(str: string): boolean {
	try {
		return btoa(atob(str)) === str;
	} catch {
		return false;
	}
}

}